import { useEffect, useState } from "react";
import { cache } from "@cornerstonejs/core";

export type MicrobleedStatus = "new" | "stable" | "not_observed" | "unknown";

export interface Microbleed {
  uid: string;
  /** Sequential ID inside this ImagingSelection list (from `instance[].number`). */
  number: number | null;
  rid: string;
  bodySiteCode: string | null;
  bodySiteDisplay: string | null;
  status: MicrobleedStatus;
  coordinateMm: [number, number, number] | null;
  /**
   * Backend-supplied voxel index. This is the SOURCE OF TRUTH for sampling
   * the segm-cmb_spatial-analysis volume and for camera focus — backend
   * generates it against that file's grid, so it's directly usable as a
   * voxel index there.
   */
  coordinateVoxel: [number, number, number] | null;
  /**
   * Spatial-region label value at this CMB's voxel in
   * segm-cmb_spatial-analysis.nii.gz (1-13 per region). Null until the volume
   * is in cache.
   */
  spatialLabel: number | null;
}

// ─── FHIR types (subset we read) ─────────────────────────────────
interface FhirCoding {
  system?: string;
  code?: string;
  display?: string;
}
interface FhirCodeableConcept {
  coding?: FhirCoding[];
  text?: string;
}
interface FhirImageRegion3D {
  regionType: string;
  coordinate: unknown[];
}
interface FhirInstance {
  uid: string;
  number?: number;
  imageRegion3D?: FhirImageRegion3D[];
}
interface FhirImagingSelection {
  resourceType: "ImagingSelection";
  identifier?: { value?: string }[];
  status?: string;
  category?: FhirCodeableConcept[];
  code?: FhirCodeableConcept;
  bodySite?: { concept?: FhirCodeableConcept };
  instance?: FhirInstance[];
}

// ─── Parsing ─────────────────────────────────────────────────────

function parseStatus(sel: FhirImagingSelection): MicrobleedStatus {
  const candidates: string[] = [];
  for (const cat of sel.category ?? []) {
    for (const c of cat.coding ?? []) {
      if (c.code) candidates.push(c.code.toLowerCase());
      if (c.display) candidates.push(c.display.toLowerCase());
    }
    if (cat.text) candidates.push(cat.text.toLowerCase());
  }
  if (sel.status) candidates.push(sel.status.toLowerCase());

  for (const v of candidates) {
    if (v.includes("new")) return "new";
    if (v.includes("stable") || v.includes("unchanged")) return "stable";
    if (v.includes("not") || v.includes("absent") || v.includes("disappear"))
      return "not_observed";
  }
  return "unknown";
}

/** Tolerant coercion — backend may send numbers, strings, or wrapper objects. */
function toNumber(c: unknown): number {
  if (typeof c === "number") return c;
  if (c && typeof c === "object") {
    const pv = (c as { parsedValue?: unknown }).parsedValue;
    if (typeof pv === "number") return pv;
    const src = (c as { source?: unknown }).source;
    if (typeof src === "string") {
      const n = Number(src);
      if (Number.isFinite(n)) return n;
    }
  }
  const n = Number(c);
  return Number.isFinite(n) ? n : NaN;
}

function parseCoordinates(sel: FhirImagingSelection): {
  mm: [number, number, number] | null;
  voxel: [number, number, number] | null;
} {
  const triples: [number, number, number][] = [];
  for (const inst of sel.instance ?? []) {
    for (const r of inst.imageRegion3D ?? []) {
      if (r.regionType !== "point" || !Array.isArray(r.coordinate)) continue;
      if (r.coordinate.length !== 3) continue;
      const nums = r.coordinate.map(toNumber) as [number, number, number];
      if (nums.every((n) => Number.isFinite(n))) triples.push(nums);
    }
  }
  if (!triples.length) return { mm: null, voxel: null };

  // Backend convention: first triple = mm, last triple = voxel index (against
  // the segm-cmb_spatial-analysis grid).
  const first = triples[0];
  const last = triples[triples.length - 1];
  if (triples.length === 1) {
    const isInt = first.every((v) => Number.isInteger(v));
    return isInt ? { mm: null, voxel: first } : { mm: first, voxel: null };
  }
  return { mm: first, voxel: last };
}

function parseImagingSelection(sel: FhirImagingSelection): Microbleed | null {
  const uid = sel.identifier?.[0]?.value;
  if (!uid) return null;

  const rid = sel.code?.coding?.[0]?.code ?? "RID4700";
  const bs = sel.bodySite?.concept?.coding?.[0];
  const { mm, voxel } = parseCoordinates(sel);
  const number = sel.instance?.[0]?.number ?? null;

  return {
    uid,
    number,
    rid,
    bodySiteCode: bs?.code ?? null,
    bodySiteDisplay: bs?.display ?? null,
    status: parseStatus(sel),
    coordinateMm: mm,
    coordinateVoxel: voxel,
    spatialLabel: null,
  };
}

// ─── Volume lookup ──────────────────────────────────────────────

function getScalarData(volume: any): ArrayLike<number> | null {
  const vm = volume?.voxelManager;
  if (vm) {
    if (typeof vm.getCompleteScalarDataArray === "function") {
      try {
        const d = vm.getCompleteScalarDataArray();
        if (d && d.length > 0) return d;
      } catch {
        /* ignore */
      }
    }
    if (vm.scalarData?.length > 0) return vm.scalarData;
    if (typeof vm.getScalarData === "function") {
      try {
        const d = vm.getScalarData();
        if (d && d.length > 0) return d;
      } catch {
        /* ignore */
      }
    }
  }
  try {
    const scalars = volume?.imageData?.getPointData?.()?.getScalars?.();
    const d = scalars?.getData?.();
    if (d && d.length > 0) return d;
  } catch {
    /* ignore */
  }
  if (volume?.scalarData?.length > 0) return volume.scalarData;
  return null;
}

function labelAtVoxel(
  volume: any,
  voxel: [number, number, number],
): number | null {
  const data = getScalarData(volume);
  if (!data) return null;

  const [dx, dy, dz] = volume.dimensions;
  const [x, y, z] = voxel;
  if (x < 0 || x >= dx || y < 0 || y >= dy || z < 0 || z >= dz) return null;
  const v = data[z * dx * dy + y * dx + x];
  return v > 0 ? Math.round(v) : null;
}

/**
 * Wait for a specific volume to be in cache. `idOrHint` is matched as a
 * SUBSTRING of volumeId, so pass the EXACT session-specific volumeId
 * (`nifti:${spatialVolumeUrl}`) to avoid picking another timepoint's volume.
 */
async function waitForVolume(
  idOrHint: string,
  timeoutMs = 30000,
  signal?: AbortSignal,
): Promise<any | null> {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    if (signal?.aborted) return null;
    const vol = cache.getVolumes().find((v) => v.volumeId.includes(idOrHint));
    if (vol) {
      const data = getScalarData(vol);
      if (data && data.length > 0) return vol;
    }
    await new Promise((r) => setTimeout(r, 250));
  }
  return null;
}

// ─── Hook ───────────────────────────────────────────────────────

export interface UseMicrobleedsResult {
  microbleeds: Microbleed[];
  isLoading: boolean;
  error: Error | null;
}

/**
 * Two-phase loader:
 *   1. Parse FHIR companion JSON → rows render immediately.
 *   2. Once the (session-specific) segm-cmb_spatial-analysis volume is in
 *      cache → re-emit rows enriched with spatial region label values.
 *
 * @param spatialVolumeId  EXACT volumeId of THIS session's spatial volume.
 *                         When omitted, falls back to a loose substring hint
 *                         (not timepoint-safe).
 */
export default function useMicrobleeds(
  companionUrl: string | null,
  spatialVolumeId?: string | null,
): UseMicrobleedsResult {
  const [microbleeds, setMicrobleeds] = useState<Microbleed[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!companionUrl) {
      setMicrobleeds([]);
      return;
    }
    const ac = new AbortController();
    setIsLoading(true);
    setError(null);

    (async () => {
      try {
        const r = await fetch(companionUrl, {
          credentials: "include",
          signal: ac.signal,
        });
        if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
        const raw = (await r.json()) as FhirImagingSelection[];

        const list = (Array.isArray(raw) ? raw : [])
          .map(parseImagingSelection)
          .filter((m): m is Microbleed => m !== null);

        if (!ac.signal.aborted) {
          setMicrobleeds(list);
          setIsLoading(false);
        }

        const vol = await waitForVolume(
          spatialVolumeId || "segm-cmb_spatial-analysis",
          30000,
          ac.signal,
        );
        if (vol && !ac.signal.aborted) {
          const enriched = list.map((m) =>
            m.coordinateVoxel
              ? { ...m, spatialLabel: labelAtVoxel(vol, m.coordinateVoxel) }
              : m,
          );
          setMicrobleeds(enriched);
        }
      } catch (e) {
        if (ac.signal.aborted) return;
        setError(e as Error);
        setIsLoading(false);
      }
    })();

    return () => ac.abort();
  }, [companionUrl, spatialVolumeId]);

  return { microbleeds, isLoading, error };
}
